/**
 * Auto chat translation (customer portal).
 *
 * - `TranslateInline` (default): renders under an INCOMING message and shows its translation into the
 *   viewer's chosen language, automatically. The original stays (rendered by the parent); this adds a
 *   translated line below it. Nothing renders on your own messages, when the feature is off/unavailable,
 *   or when the message is already in your language.
 * - `TranslateBar`: a small header control to pick the language / turn auto-translate off. It writes the
 *   shared per-device preference, so every open chat updates together.
 */
import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { getPref, setPref, subscribePref, TRANSLATE_LANGS, getInputLang, setInputLang } from '../../utils/translatePref';

// Cache within the session so re-renders don't re-hit the API (backend caches durably too).
const cache = new Map<string, string>();

/** Header control: language picker + on/off, per conversation. Shown only when translation is available. */
export const TranslateBar: React.FC<{ enabled: boolean; convId?: string }> = ({ enabled, convId }) => {
  const [, force] = useState(0);
  useEffect(() => subscribePref(() => force((n) => n + 1)), []);
  if (!enabled) return null;
  const pref = getPref(convId);
  const inLang = getInputLang(convId);
  return (
    <div style={barCol}>
      {/* Receiver side: translate INCOMING messages into your language. */}
      <div style={barWrap}>
        <span style={barLabel}>🌐 Translate to</span>
        <select
          value={pref.lang}
          onChange={(e) => setPref(convId, { lang: e.target.value, on: true })}
          style={barSelect}
          aria-label="Translate incoming messages to"
        >
          {TRANSLATE_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        <button type="button" onClick={() => setPref(convId, { on: !pref.on })} style={{ ...barToggle, ...(pref.on ? barToggleOn : {}) }}>
          {pref.on ? 'On' : 'Off'}
        </button>
      </div>
      {/* Sender side: the language YOU type in — sent as a hint so the other side reads it accurately. */}
      <div style={barWrap}>
        <span style={barLabel}>✍️ I type in</span>
        <select
          value={inLang}
          onChange={(e) => setInputLang(convId, e.target.value)}
          style={barSelect}
          aria-label="The language you type in"
        >
          <option value="">Auto-detect</option>
          {TRANSLATE_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </div>
    </div>
  );
};

const TranslateInline: React.FC<{ text: string; enabled: boolean; branchId?: string; isIncoming?: boolean; convId?: string; sourceLang?: string }> = ({ text, enabled, branchId, isIncoming, convId, sourceLang }) => {
  const [, force] = useState(0);
  const [translated, setTranslated] = useState<string | null>(null);
  useEffect(() => subscribePref(() => force((n) => n + 1)), []);

  const pref = getPref(convId);
  const active = enabled && !!isIncoming && pref.on && !!branchId && !!(text && text.trim());

  useEffect(() => {
    let alive = true;
    if (!active) { setTranslated(null); return; }
    const key = `${pref.lang}|${sourceLang || ''}|${text}`;
    const hit = cache.get(key);
    if (hit != null) { setTranslated(hit.trim() && hit.trim() !== text.trim() ? hit : null); return; }
    apiService.translateText(text, pref.lang, branchId, sourceLang).then((res) => {
      if (!alive) return;
      cache.set(key, res);
      setTranslated(res && res.trim() && res.trim() !== text.trim() ? res : null);
    }).catch(() => { if (alive) setTranslated(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, pref.lang, text, branchId, sourceLang]);

  if (!active || !translated) return null;
  return (
    <div style={line}>
      <span style={tag}>🌐</span>
      <span>{translated}</span>
    </div>
  );
};

const line: React.CSSProperties = { marginTop: 4, display: 'flex', gap: 6, fontSize: '0.9rem', lineHeight: 1.35, color: '#e0531f', background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.28)', borderRadius: 8, padding: '5px 8px' };
const tag: React.CSSProperties = { flexShrink: 0, opacity: 0.8 };

const barCol: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, margin: '0 0 8px' };
const barWrap: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.28)', borderRadius: 10 };
const barLabel: React.CSSProperties = { fontSize: '0.78rem', fontWeight: 700, color: '#e0531f' };
const barSelect: React.CSSProperties = { fontSize: '0.78rem', padding: '3px 8px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a' };
const barToggle: React.CSSProperties = { marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 800, borderRadius: 9999, padding: '3px 12px', border: '1px solid #cbd5e1', background: '#e2e8f0', color: '#475569', cursor: 'pointer' };
const barToggleOn: React.CSSProperties = { background: '#e0531f', color: '#fff', borderColor: '#e0531f' };

export default TranslateInline;
